<template>
  <div class="pdf-mapper-component mt-4 border-top pt-4">
    <h5 class="text-primary mb-3">
      <i class="fas fa-exchange-alt mr-2"/>
      Mapeamento de Dados
    </h5>
    <p class="text-muted small mb-4">Ligue as chaves encontradas no PDF de teste aos campos do sistema.</p>

    <d-row class="m-0 border rounded shadow-sm">
      <d-col md="3" class="bg-light border-right p-0">
        <ul class="crm-tabs list-unstyled m-0">
          <li v-for="(fields, tabName) in dynamicSchema" :key="tabName"
              :class="['crm-tab-item', { 'active': activeTab === tabName }]"
              @click="activeTab = tabName">
            {{ tabName }}
          </li>
        </ul>
      </d-col>

      <d-col md="9" class="p-4 bg-white" v-if="dynamicSchema[activeTab]">
        <h6 class="text-primary border-bottom pb-2 mb-3">{{ activeTab }}</h6>

        <d-row class="mb-2 pb-2 border-bottom text-muted font-weight-bold x-small text-uppercase">
          <d-col md="5">Campo do Sistema</d-col>
          <d-col md="7">Dado do PDF</d-col>
        </d-row>

        <d-row v-for="field in dynamicSchema[activeTab]" :key="field.path" class="mapping-row align-items-center py-2">
          <d-col md="5">
            <span class="font-weight-bold text-dark d-block">{{ field.label }}</span>
          </d-col>

          <d-col md="7">
            <d-select class="form-control form-control-sm"
              :value="localMapping[field.path]"
              @change="updateMapping(field.path, $event)"
            >
              <option :value="undefined">Não mapear</option>

              <optgroup v-for="(group, groupName) in pdfOptions" :key="groupName" :label="groupName">
                <option v-for="opt in group" :key="opt.value" :value="opt.value">
                  {{ opt.label }} <span v-if="opt.preview">(Ex: {{ opt.preview }})</span>
                </option>
              </optgroup>
            </d-select>
          </d-col>
        </d-row>
      </d-col>
    </d-row>
  </div>
</template>

<script>
import * as Models from '../../sample/components/SampleModel';

export default {
  name: 'pdf-mapper',
  props: {
    pdfData: {
      type: Object,
      required: true,
    },
    mapping: {
      type: Object,
      default: () => ({}),
    },
    templateType: {
      type: String,
      default: 'HEAT',
    },
  },
  data() {
    return {
      activeTab: 'Caracterização',
      localMapping: { ...this.mapping },
    };
  },
  computed: {
    dynamicSchema() {
      const type = this.templateType.toUpperCase();

      const memoryModel = Models[`${type}_MEMORY`] || {};
      const resultModel = Models[`${type}_RESULT`] || {};
      const classModel = Models[`${type}_CLASSIFICATION`] || {};

      return {
        Caracterização: this.extractFields(Models.CHARACTERIZATION, 'characterization'),
        'Memória de Campo': this.extractFields(memoryModel, 'memory_field'),
        Resultado: this.extractFields(resultModel, 'result'),
        Classificação: this.extractFields(classModel, 'risk_classification'),
      };
    },
    pdfOptions() {
      const groups = { 'Campos Principais': [] };
      if (!this.pdfData) return groups;
      this.buildFields(groups);
      this.buildTables(groups);
      return groups;
    },
  },
  methods: {
    getFriendlyLabel(key) {
      if (Models.FIELD_DICTIONARY && Models.FIELD_DICTIONARY[key]) {
        return Models.FIELD_DICTIONARY[key];
      }
      return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
    },
    extractFields(obj, prefix = '') {
      if (!obj) return [];
      const fields = [];
      Object.keys(obj).forEach(key => {
        const val = obj[key];
        const currentPath = prefix ? `${prefix}.${key}` : key;
        const parentLabel = this.getFriendlyLabel(key);

        if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'object') {
          Object.keys(val[0]).forEach(subKey => {
            fields.push({ path: `${currentPath}[].${subKey}`, label: `${parentLabel} ➔ ${this.getFriendlyLabel(subKey)}` });
          });
        } else if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
          Object.keys(val).forEach(subKey => {
            fields.push({ path: `${currentPath}.${subKey}`, label: `${parentLabel} ➔ ${this.getFriendlyLabel(subKey)}` });
          });
        } else {
          fields.push({ path: currentPath, label: parentLabel });
        }
      });
      return fields;
    },
    updateMapping(path, eventValue) {
      if (!eventValue || eventValue === 'undefined') {
        this.$delete(this.localMapping, path);
      } else {
        this.$set(this.localMapping, path, eventValue);
      }

      this.$emit('update-mapping', { ...this.localMapping });
    },
    buildFields(groups) {
      if (!this.pdfData.fields) return;
      Object.entries(this.pdfData.fields).forEach(([key, val]) => {
        groups['Campos Principais'].push({ value: `fields.${key}`, label: key, preview: val });
      });
    },
    buildTables(groups) {
      if (!this.pdfData.tables) return;
      Object.entries(this.pdfData.tables).forEach(([tableName, rows]) => {
        if (rows.length > 0) {
          const groupName = `Tabela: ${tableName.replace(/_/g, ' ')}`;
          groups[groupName] = [];
          Object.entries(rows[0]).forEach(([colKey, colVal]) => {
            groups[groupName].push({ value: `tables.${tableName}.${colKey}`, label: colKey, preview: colVal });
          });
        }
      });
    },
  },
  watch: {
    mapping: {
      deep: true,
      handler(newVal) {
        this.localMapping = { ...newVal };
      },
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~_scss/overrides';

.crm-tabs {
  height: 100%;
}
.crm-tab-item {
  padding: 12px 15px;
  cursor: pointer;
  border-bottom: 1px solid #f8f9fa;
  color: #555; font-size: 13px;
}
.crm-tab-item:hover {
  background-color: #f1f5f9;
}
.crm-tab-item.active {
  background-color: #fff;
  color: $primary;
  font-weight: bold;
  border-left: 3px solid $primary;
}
.mapping-row {
  border-bottom: 1px dashed #eaeaea;
}
.mapping-row:hover {
  background-color: #fcfcfc;
}
.x-small {
  font-size: 10px;
  letter-spacing: 0.5px;
}
</style>
